import type { LocalStorageKeys, User } from '@/types/auth';

/**
 * Auth helper.
 * @description This is a helper object to store or retrieve the auth token, refresh token and user in the local storage.
 * @example
 * authHelper.token = '1234567890';
 * authHelper.refreshToken = '1234567890';
 * authHelper.user = { id: 1, name: 'John Doe' };
 */
export const authHelper = {
  token: getLocalStorageItem<string>('token') || null,
  refreshToken: getLocalStorageItem<string>('refreshToken') || null,
  user: getLocalStorageItem<User>('user') || null,
};
/**
 * Token helper.
 * @description This is a helper object to store or retrieve the token in the local storage.
 */
Object.defineProperty(authHelper, 'token', {
  get: () => {
    return getLocalStorageItem<string>('token');
  },
  set: (value: string) => {
    setLocalStorageItem('token', value);
  },
});
/**
 * Refresh token helper.
 * @description This is a helper object to store or retrieve the refresh token in the local storage.
 */
Object.defineProperty(authHelper, 'refreshToken', {
  get: () => {
    return getLocalStorageItem<string>('refreshToken');
  },
  set: (value: string) => {
    setLocalStorageItem('refreshToken', value);
  },
});
/**
 * User helper.
 * @description This is a helper object to store or retrieve the user in the local storage.
 */
Object.defineProperty(authHelper, 'user', {
  get: () => {
    return getLocalStorageItem<User>('user');
  },
  set: (value: User) => {
    setLocalStorageItem('user', JSON.stringify(value));
  },
});

/**
 * Get item from local storage.
 * @description This is a helper function to get an item from the local storage.
 * @param key - The key of the item to get.
 * @returns The item from the local storage.
 */
export function getLocalStorageItem<T = string>(key: LocalStorageKeys): T | null {
  // if item is not found, return null
  const item = localStorage.getItem(key);
  if (!item) return null;
  try {
    // if item is a string, return it
    const res = JSON.parse(item) as T;
    if (typeof res === 'object') {
      return res;
    }
    // if item is not a string, return it
    return item as T;
  } catch (error) {
    return item as T;
  }
}

/**
 * Set item in local storage.
 * @description This is a helper function to set an item in the local storage.
 * @param key - The key of the item to set.
 * @param value - The value of the item to set.
 */
export function setLocalStorageItem(key: LocalStorageKeys, value: string) {
  localStorage.setItem(key, value);
}

/**
 * Remove item from local storage.
 * @description This is a helper function to remove an item from the local storage.
 * @param key - The key of the item to remove.
 */
export function removeLocalStorageItem(key: LocalStorageKeys) {
  localStorage.removeItem(key);
}
